import { useCallback, useEffect, useMemo, useState } from "react";
import Map from "common/components/map/Map";
import { Point } from "common/components/map/Point.type";
import { Thread } from "types/Thread";
import DebounceInput from "common/components/DebounceInput/DebounceInput";
import StickyWrapper from "common/components/StickyWrapper/StickyWrapper";
import { Weather } from "types/Weather";
import useList from "common/hooks/useList";
import dayjs from "dayjs";
import { Constants } from "constants/Constants";
import ThreadMapItem from "./ThreadMapItem";

type Props = {
  threads: Thread[];
  columns: any[];
  filters: { [key: string]: any };
  setFilter?: (filter: any, value: any) => void;
  setPageIndex?: (pageIndex: number) => void;
};

export default function MapScreen({
  threads,
  columns = [],
  filters,
  setFilter,
  setPageIndex,
}: Props) {
  const [selectedThread, setSelectedThread] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    items: weatherItems,
    isFetching: weatherLoading,
    setFilters: setWeatherFilters,
  } = useList<Weather>("weathers", {
    defaultSort: "date:ASC",
    ready: Boolean(selectedThread?.createdAt),
    cachePrefix: `ContactGroups_${selectedThread?.id || "all"}`,
  });

  useEffect(() => {
    if (!selectedThread?.createdAt) {
      return;
    }
    const baseDate = dayjs(selectedThread.createdAt).startOf("hour");

    setWeatherFilters({
      date$gte: baseDate.subtract(1, "hour").toISOString(),
      date$lte: baseDate.add(1, "hour").add(20, "minute").toISOString(),
    });
  }, [selectedThread, setWeatherFilters]);

  const points: Point[] = useMemo(
    () =>
      threads
        ?.map((thread: Thread) => {
          return {
            id: thread.id,
            lat: thread.geojson?.lat,
            lng: thread.geojson?.lng,
            name: thread?.messages?.[0]?.content,
            type: thread.type,
          } as Point;
        })
        .filter((p: Point) => Boolean(p.lat) && Boolean(p.lng)) || [],
    [threads]
  );

  // Debug logs
  // console.log("Points count:", points?.length);
  // console.log("Points data:", points);
  // console.log("Selected thread:", selectedThread);

  const handleMarkerPress = useCallback(
    (e: any) => {
      // not exactly same than native

      const thread = threads?.find(
        (iThread: Thread) => iThread.id.toString() === e.toString()
      );

      setSelectedThread(thread);
      if (thread) {
        setIsDrawerOpen(true);
      }
    },
    [threads]
  );

  const setSelectedThreadFromPointId = useCallback(
    (pointId: string | number) => {
      const thread = threads?.find(
        (iThread: Thread) => iThread.id.toString() === pointId.toString()
      );
      setSelectedThread(thread);
      setIsDrawerOpen(Boolean(thread));
    },
    [threads]
  );

  /* const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedThread(null); // Immediately clear selection
  }, []); */

  const handleChangeFilter = useCallback(
    (name: string, value: any) => {
      // Reset page to 0 when filter changes
      if (setPageIndex) {
        setPageIndex(0);
      }
      if (setFilter) {
        setFilter(name, value);
      }
    },
    [setFilter, setPageIndex]
  );

  return (
    <div className="flex flex-col h-[calc(100vh-9.8rem)]">
      {/* Filter UI */}
      {columns.length > 0 && (
        <StickyWrapper isFilters disabledOnMobile>
          <div className="flex w-full justify-center px-4 py-3 border border-t-0 border-slate-150 rounded-b-lg mb-2 z-10 bg-slate-100">
            <div className="w-full flex flex-col gap-4 lg:flex-row lg:flex-wrap align-center">
              {columns.map((column: any, key: any) =>
                column.hidden ? null : (
                  <div
                    key={`column-${column.filterName || key}`}
                    className={`py-0 flex flex-col ${column.columnClasses} maxLg:w-full`}
                  >
                    {column.filterName &&
                      (column.filterComponent ? (
                        <column.filterComponent
                          placeholder={column.Header}
                          value={filters[column.filterName]}
                          data-cy={column.filterName}
                          onChange={(value: any) => {
                            handleChangeFilter(
                              column.filterName,
                              column.customFormat
                                ? column.customFormat(value)
                                : value
                            );
                          }}
                        />
                      ) : (
                        <DebounceInput
                          className="text-input-filter"
                          placeholder={column.Header}
                          value={filters[column.filterName]}
                          onChange={(value) =>
                            handleChangeFilter(column.filterName, value)
                          }
                        />
                      ))}
                  </div>
                )
              )}
            </div>
          </div>
          {points && points.length > 500 && (
            <div className="mt-1 mb-2 bg-white rounded-md border-l-4 border-blue-500 px-4 py-2">
              <p className="text-sm text-slate-700">
                Cette carte affiche les <strong>500 échanges</strong> les plus
                récents. Pour une recherche plus ancienne, veuillez passer en
                vue Liste.
              </p>
            </div>
          )}
        </StickyWrapper>
      )}

      <div className="flex-1 relative">
        <Map
          height={undefined}
          points={points}
          onMarkerPress={handleMarkerPress}
          zoomLvl={10} // used if geolocation
          centeredOnUser={points.length === 0} // Only center on user if no points
          selectedPointId={selectedThread?.id}
          setSelectedPointId={setSelectedThreadFromPointId}
          initialRegion={{
            id: "initial-region",
            lat: parseFloat(Constants.MAP_DEFAULT_LAT),
            lng: parseFloat(Constants.MAP_DEFAULT_LNG),
          }}
        />

        {/* Drawer with handle */}
        {selectedThread && (
          <div
            className={`absolute right-4 top-4 rounded-t-xl shadow-2xl max-w-[40vw] max-h-[70vh] overflow-hidden transition-transform duration-300 ease-in-out ${
              isDrawerOpen ? "translate-y-0" : "translate-y-full"
            }`}
            onClick={(e) => e.stopPropagation()} // prevent drawer clicks from propagating to map
          >
            <div className="overflow-y-auto shadow-xl mx-auto w-[35vw] ">
              <ThreadMapItem
                item={selectedThread}
                weatherInfo={weatherItems}
                weatherLoading={weatherLoading}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
